import {
  Select,
  Box,
  H3,
  MaterialTableWrapper,
  MRT_TableOptions,
  MRT_ColumnDef,
  MRT_Row,
  withThemeProvider,
  OusterProvider,
} from '@ouster/webviz-react-components';
import type { MaterialTableWrapperProps } from '@ouster/webviz-react-components';

import { IconButton, Tooltip } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { RiDeleteBin5Line, RiMailSendLine } from 'react-icons/ri';

import styles from './Organization.module.css';
import type { UserWithRole, UserRole } from '../../types';
import { formatUserRoleLabel, getAssignableRoles } from '../../utils/roles';

type OrganizationMembersProps = {
  isAdmin: boolean;
  canAssignSuperAdmin: boolean;
  orgUserList: UserWithRole[];
  curUser: UserWithRole;
  isOusterUser: boolean;
  isProvisioned?: boolean;
  deleteUserHandler: (member: UserWithRole) => void;
  updateUserRoleHandler: (member: UserWithRole, role: UserRole) => void;
  reinviteHandler: (uuid: string) => void;
};

/* 
  cell component for change role permission
*/
type RoleSelectCellProps = {
  isAdmin: boolean;
  isOusterUser: boolean;
  canAssignSuperAdmin: boolean;
  curUser: UserWithRole;
  isProvisioned?: boolean;
  row: MRT_Row<UserWithRole>;
  updateUserRoleHandler: (member: UserWithRole, role: UserRole) => void;
};

const RoleSelectCell = ({
  isAdmin,
  isOusterUser,
  canAssignSuperAdmin,
  curUser,
  isProvisioned = false,
  row,
  updateUserRoleHandler,
}: RoleSelectCellProps) => {
  const member = row.original;
  const value = member.role;
  const { email } = member;
  const invitationStatus = member.invitation_status;
  const renderedRoles = getAssignableRoles({
    isOusterUser,
    canAssignSuperAdmin,
    currentRole: value,
  });
  const isSuperAdminRoleLocked =
    value === 'SUPER_ADMIN' && !canAssignSuperAdmin;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      updateUserRoleHandler(member, e.target.value as UserRole);
    },
    [member, updateUserRoleHandler]
  );

  return (
    <OusterProvider>
      <Select
        textTransform="capitalize"
        defaultValue={value}
        isDisabled={
          !isAdmin ||
          curUser?.email === email ||
          invitationStatus === 'CREATED' ||
          isProvisioned ||
          isSuperAdminRoleLocked
        }
        onChange={handleChange}
        data-testid="role-select"
      >
        {renderedRoles.map((role) => (
          <option
            key={role}
            value={role}
            data-testid={`role-option-${role.toLowerCase()}`}
          >
            {formatUserRoleLabel(role)}
          </option>
        ))}
      </Select>
    </OusterProvider>
  );
};

/* 
  cell component for action columns: re-invite and delete user
*/

type ActionsCellProps = {
  isAdmin: boolean;
  curUser: UserWithRole;
  row: MRT_Row<UserWithRole>;
  deleteUserHandler: (member: UserWithRole) => void;
  reinviteHandler: (uuid: string) => void;
};
const ActionsCell = ({
  isAdmin,
  curUser,
  row,
  deleteUserHandler,
  reinviteHandler,
}: ActionsCellProps) => (
  <Box
    flexWrap="nowrap"
    display="flex"
    columnGap={2}
    data-testid={`actions-cell-${row.original.email}`}
  >
    <Tooltip
      arrow
      title={
        !isAdmin || curUser?.email === row.original.email ? '' : 'Remove member'
      }
    >
      <span>
        <IconButton
          onClick={() => deleteUserHandler(row.original)}
          disabled={!isAdmin || curUser?.email === row.original.email}
          data-testid="role-delete-member"
          aria-label="Remove member"
        >
          <RiDeleteBin5Line />
        </IconButton>
      </span>
    </Tooltip>
    {row.original.invitation_status === 'CREATED' && (
      <Tooltip arrow title={isAdmin ? 'Reinvite member' : ''}>
        <span>
          <IconButton
            onClick={() =>
              row.original?.invitation_link &&
              reinviteHandler(row.original.invitation_link)
            }
            data-testid="role-reinvite-member"
            aria-label="Reinvite member"
            disabled={!isAdmin}
          >
            <RiMailSendLine />
          </IconButton>
        </span>
      </Tooltip>
    )}
  </Box>
);

const ThemedMaterialTableWrapper =
  withThemeProvider<MaterialTableWrapperProps<UserWithRole>>(
    MaterialTableWrapper
  );

export default function OrganizationMembers({
  isAdmin,
  canAssignSuperAdmin,
  orgUserList,
  curUser,
  isOusterUser,
  isProvisioned,
  deleteUserHandler,
  updateUserRoleHandler,
  reinviteHandler,
}: OrganizationMembersProps) {
  const renderRoleCell = useCallback(
    ({ row }: { row: MRT_Row<UserWithRole> }) => (
      <RoleSelectCell
        isAdmin={isAdmin}
        isOusterUser={isOusterUser}
        canAssignSuperAdmin={canAssignSuperAdmin}
        curUser={curUser}
        isProvisioned={isProvisioned}
        row={row}
        updateUserRoleHandler={updateUserRoleHandler}
      />
    ),
    [
      isAdmin,
      isOusterUser,
      canAssignSuperAdmin,
      curUser,
      updateUserRoleHandler,
      isProvisioned,
    ]
  );

  const renderRowActions = useCallback(
    ({ row }: { row: MRT_Row<UserWithRole> }) => (
      <OusterProvider>
        <ActionsCell
          isAdmin={isAdmin}
          curUser={curUser}
          row={row}
          deleteUserHandler={deleteUserHandler}
          reinviteHandler={reinviteHandler}
        />
      </OusterProvider>
    ),
    [isAdmin, curUser, deleteUserHandler, reinviteHandler]
  );

  const columns = useMemo<MRT_ColumnDef<UserWithRole>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ cell, row }) => {
          const name = cell.getValue();
          const { email } = row.original;
          return (name || email) as React.ReactNode;
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'role',
        header: 'Role',
        Cell: renderRoleCell,
      },
      {
        header: 'Status',
        accessorFn: (row) =>
          row.invitation_status === 'CREATED' ? 'Invited' : 'Joined',
        // Cast the unknown value to a ReactNode
        Cell: ({ cell }) => cell.getValue() as React.ReactNode,
      },
    ],
    [renderRoleCell]
  );

  const tableOptions: MRT_TableOptions<UserWithRole> = useMemo(
    () => ({
      columns,
      data: orgUserList,
      initialState: {
        pagination: { pageSize: 10, pageIndex: 0 },
        columnPinning: { right: ['mrt-row-actions'], left: ['name'] },
        showGlobalFilter: true,
      },
      enableFilterMatchHighlighting: false,
      enableRowActions: true,
      positionGlobalFilter: 'left',
      enableSorting: true,
      enableToolbarInternalActions: false,
      positionActionsColumn: 'last',
      getRowId: (originalRow) => originalRow.email,
      renderRowActions,
    }),
    [columns, orgUserList, renderRowActions]
  );

  return (
    <div
      className={styles['section-container']}
      data-testid="org-members-section"
    >
      <H3
        fontSize={16}
        textTransform="uppercase"
        className={styles.heading}
        data-testid="org-members-heading"
      >
        Organization Members
      </H3>
      <ThemedMaterialTableWrapper {...tableOptions} />
    </div>
  );
}
